export type Gender = 'm' | 'f';
export type Noun = { word: string; g: Gender };
export type Adjective = string | { m: string; f: string };
export type PoolKey = 'br' | 'ar';

export type Pool = {
  key: PoolKey;
  /** T1 / T4 / T6 nouns. */
  animals: Noun[];
  /** T2 nouns — curated diminutives / augmentatives. */
  petForms: Noun[];
  /** T1 / T2 and T4 person adjectives. */
  adjectives: Adjective[];
  /** T3 / T4 nouns. */
  foods: Noun[];
  /** T3 adjectives only. */
  foodAdjectives: Adjective[];
  /** T4 `title.noun`: `any` pairs with either gender, `m`/`f` only with that gender. */
  titles: { any: string[]; m: string[]; f: string[] };
  /** T4 `person.adjective`. */
  persons: Noun[];
  /** T5 `title.<article>.<thing>`. */
  t5Titles: { m: string[]; f: string[] };
  /** Article by the thing's gender: BR do/da, AR del/de.la. */
  t5Article: { m: string; f: string };
  t5Things: { m: string[]; f: string[] };
  /** Emitted verbatim as T5 draws. */
  idioms: string[];
  /** T6 adjectives. */
  regionals: Adjective[];
  /** Never allowed into this pool; pools.test.ts enforces it. */
  excluded: string[];
};

export const m = (word: string): Noun => ({ word, g: 'm' });
export const f = (word: string): Noun => ({ word, g: 'f' });
/** `'sortudo/sortuda'` → gendered; `'feliz'` → invariable. */
export const adj = (spec: string): Adjective => {
  const [mm, ff] = spec.split('/');
  return ff ? { m: mm, f: ff } : mm;
};

export function agree(adjective: Adjective, g: Gender): string {
  return typeof adjective === 'string' ? adjective : adjective[g];
}

function adjectiveWords(list: Adjective[]): string[] {
  return list.flatMap((a) => (typeof a === 'string' ? [a] : [a.m, a.f]));
}

/** Every single word that can appear in a generated name (idioms excluded). */
export function allWords(pool: Pool): string[] {
  return [
    ...pool.animals.map((n) => n.word),
    ...pool.petForms.map((n) => n.word),
    ...adjectiveWords(pool.adjectives),
    ...pool.foods.map((n) => n.word),
    ...adjectiveWords(pool.foodAdjectives),
    ...pool.titles.any, ...pool.titles.m, ...pool.titles.f,
    ...pool.persons.map((n) => n.word),
    ...pool.t5Titles.m, ...pool.t5Titles.f,
    ...pool.t5Things.m, ...pool.t5Things.f,
    ...adjectiveWords(pool.regionals),
  ];
}
